import type { CommonOptions } from "@shared/ui";
import { __ } from "@shared/ui";
import autobind from "autobind-decorator";
import compact from "lodash/compact";
import type React from "react";
import type { InputHTMLAttributes, ReactNode } from "react";
import { Form } from "react-bootstrap";
import type { FieldRenderProps } from "react-final-form";
import type { FormInputProps, FormInputState } from "./FormInput";
import { FormInput } from "./FormInput";

export interface FormNumberProps extends FormInputProps<number> {
	isInteger?: boolean;
	step?: number;
	min?: number;
	max?: number;
}

export class FormNumber<
	Props extends FormNumberProps = FormNumberProps,
	State extends FormInputState = FormInputState,
> extends FormInput<number, Props, State> {
	public static defaultProps: Partial<FormNumberProps> = {
		type: "number",
	};

	@autobind
	protected parse(value: any): number {
		const { type } = this.props;

		if ((type as string) === "number" && value !== "") {
			return this.valueCalculator(Number(value));
		}

		return super.parse(value);
	}

	protected renderInput(fieldProps: FieldRenderProps<number>, additionalClass?: string): ReactNode {
		const { dataList, disabled, size, placeholder, required, readOnly, tabIndex, min, max, step } = this.props;
		const {
			input: { name, value },
			meta: { invalid, touched },
		} = fieldProps;

		const input = (
			<Form.Control
				{...this.getInputProps(fieldProps)}
				ref={this.inputElRef}
				className={dataList || (min && max && step) ? "d-md-block d-none" : additionalClass}
				size={size}
				value={value === undefined ? "" : (value as unknown as string)}
				placeholder={placeholder}
				isInvalid={!disabled && touched && invalid}
				onChange={(event) => {
					this.handleChange(
						this.valueCalculator((event.currentTarget as HTMLInputElement).value as unknown as number),
						fieldProps,
					);
				}}
				onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
					this.handleFocus(event, fieldProps);
				}}
				onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
					this.handleBlur(event, fieldProps);
				}}
			/>
		);

		if (dataList || (min && max && step)) {
			const options: CommonOptions<number, number> = [];

			if (dataList) {
				dataList.forEach((i) =>
					options.push({
						label: i,
						value: i,
					}),
				);
			} else {
				for (let i = min; i <= this.getMaxValue(); i += step) {
					options.push({ label: i, value: i });
				}
			}

			const className = compact([
				"custom-select",
				placeholder && "has-placeholder",
				placeholder && value === undefined && "placeholder",
				"d-md-none",
			]).join(" ");

			return (
				<>
					{input}
					<Form.Control
						name={name}
						className={className}
						as="select"
						size={size}
						readOnly={readOnly}
						disabled={disabled || readOnly}
						tabIndex={tabIndex}
						required={required}
						// isValid={!disabled && touched && valid}
						isInvalid={!disabled && touched && invalid}
						value={value as unknown as string}
						onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
							const element = event.currentTarget;
							const index = placeholder ? element.selectedIndex - 1 : element.selectedIndex;
							const newValue = options[index]?.value;

							this.handleChange(newValue, fieldProps);
						}}
						onFocus={(event: React.FocusEvent<HTMLSelectElement>) => {
							this.handleFocus(event, fieldProps);
						}}
						onBlur={(event: React.FocusEvent<HTMLSelectElement>) => {
							this.handleBlur(event, fieldProps);
						}}
					>
						{placeholder && (
							<option value="" disabled={value !== undefined}>
								{placeholder}
							</option>
						)}
						{options.map((option) => (
							<option key={String(option.value)} value={String(option.value)} disabled={option.disabled}>
								{option.label}
							</option>
						))}
					</Form.Control>
				</>
			);
		}

		return input;
	}

	private valueCalculator(value: number): number {
		if (!value) {
			return value;
		}

		if (!this.props.isInteger) {
			const valueString = `${value}`.replace(",", ".");

			if (valueString.endsWith(".")) {
				return value;
			}

			return Number(valueString);
		}

		const newValue = Number(`${value}`.replace(/[.,]+/, ""));

		if (!isNaN(newValue)) {
			return newValue;
		}

		return value;
	}

	private getMaxValue(): number {
		const { maxLength } = this.props;
		let { max } = this.props;

		if (max === undefined && maxLength !== undefined) {
			max = Number(new Array(maxLength).fill(9).join(""));
		}

		return max;
	}

	@autobind
	protected handleValidate(value: number, allValues: Record<string, any>): string {
		const { required } = this.props;
		const maxValue = this.getMaxValue();

		if (this.isDisabled()) {
			return undefined;
		}

		if (
			required &&
			((typeof value === "number" && isNaN(value)) ||
				(typeof value === "boolean" && value === undefined) ||
				(typeof value !== "number" && typeof value !== "boolean" && !value))
		) {
			return __("Adj meg egy értéket!");
		}

		if (maxValue !== undefined && value > maxValue) {
			return `A mező értéke nem lehet több mint ${maxValue}!`;
		}

		return super.handleValidate(value, allValues);
	}

	protected getInputProps(fieldProps: FieldRenderProps<number>): InputHTMLAttributes<HTMLInputElement> {
		const { min, step } = this.props;

		return {
			...super.getInputProps(fieldProps),
			min,
			max: this.getMaxValue(),
			step,
		};
	}
}
